import { useState } from "react";
import { Button, Input } from "@nextui-org/react";

import type { AdminRegistration } from "@/types";

import {
  BackArrowIcon,
  VisibilityOffIcon,
  VisibilityOnIcon,
} from "@/assets/icons/admin";

interface ResetAccountPwProps {
  item: AdminRegistration;
  onCancel: () => void;
}

const ResetAccountPw: React.FC<ResetAccountPwProps> = ({ onCancel }) => {
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(true);
  const [confirmPW, setConfirmPW] = useState("");
  const [isConfirmPWValid, setIsConfirmPWValid] = useState(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode] = useState("");
  const isVerificationCodeValid = true;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "newPassword") {
      setNewPassword(value);
      setIsNewPasswordValid(true);
    } else if (name === "confirmPW") {
      setConfirmPW(value);
      setIsConfirmPWValid(true);
    }
  };

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSave = () => {
    const isPasswordValid = /^(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/.test(
      newPassword,
    );
    setIsNewPasswordValid(isPasswordValid);

    const isConfirmPWMatch = newPassword === confirmPW;
    setIsConfirmPWValid(isConfirmPWMatch);
  };

  return (
    <div>
      <div className="font-ubuntu mb-3 w-full text-center text-2xl font-semibold text-white sm:float-left sm:mb-3">
        <span>
          <Button
            isIconOnly
            onClick={onCancel}
            className="transparent-bg float-left ml-[-11px] h-auto w-auto"
          >
            <BackArrowIcon />
          </Button>
          <p className="text-sm sm:float-left sm:text-lg md:text-lg lg:text-lg">
            Reset Password
          </p>
        </span>
      </div>
      <div className="flex w-full flex-col gap-6">
        <div className="flex flex-row">
          <div className="w-full ">
            <label
              htmlFor="firstName"
              className="mb-2 field-label block text-sm font-normal text-white"
            >
              Old Password
            </label>
            <Input
              name="oldpassword"
              type={showOldPassword ? "text" : "password"}
              className={`input-bg !cursor-text rounded-md border border-solid`}
              classNames={{
                inputWrapper: [
                  "!cursor-text",
                  "rounded-md",
                  "border",
                  "border-solid",
                  "border-white",
                  "border-opacity-50",
                  "bg-white-5",
                ],
              }}
              endContent={
                <button
                  className="focus:outline-none"
                  type="button"
                  onClick={toggleOldPasswordVisibility}
                >
                  {showOldPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityOnIcon />
                  )}
                </button>
              }
            ></Input>
          </div>
        </div>
        <div className="flex flex-row">
          <div className="w-full">
            <label
              htmlFor="firstName"
              className="mb-2 field-label block text-sm font-normal text-white"
            >
              New Password
            </label>
            <Input
              name="newPassword"
              value={newPassword}
              onChange={handleInputChange}
              type={showNewPassword ? "text" : "password"}
              className={`rounded-md border border-solid ${isNewPasswordValid ? "input-bg" : "validation"} !cursor-text`}
              classNames={{
                inputWrapper: [
                  "!cursor-text",
                  "rounded-md",
                  "border",
                  "border-solid",
                  "border-white",
                  "border-opacity-50",
                  "bg-white-5",
                ],
              }}
              endContent={
                <button
                  className="focus:outline-none"
                  type="button"
                  onClick={toggleNewPasswordVisibility}
                >
                  {showNewPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityOnIcon />
                  )}
                </button>
              }
            ></Input>
            {!isNewPasswordValid && (
              <p className="validation-txt mt-1 text-sm">
                *Password need to be at least 8 characters, 1 number, 1 capital
                letters, and no special characters
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-row">
          <div className="w-full">
            <label
              htmlFor="firstName"
              className="mb-2 field-label block text-sm font-normal text-white"
            >
              Confirm Password
            </label>
            <Input
              name="confirmPW"
              value={confirmPW}
              onChange={handleInputChange}
              type={showConfirmPassword ? "text" : "password"}
              className={`rounded-md border border-solid ${isConfirmPWValid ? "input-bg" : "validation"} !cursor-text`}
              classNames={{
                inputWrapper: [
                  "!cursor-text",
                  "rounded-md",
                  "border",
                  "border-solid",
                  "border-white",
                  "border-opacity-50",
                  "bg-white-5",
                ],
              }}
              endContent={
                <button
                  className="focus:outline-none"
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                >
                  {showConfirmPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityOnIcon />
                  )}
                </button>
              }
            ></Input>
            {!isConfirmPWValid && (
              <p className="validation-txt mt-1 text-sm">
                *The Password doesn’t match. Please try again.
              </p>
            )}
          </div>
        </div>
        {/* hidden on larger screens */}
        <div className="flex flex-row sm:hidden">
          <div className="basis-1/2">
            <label
              htmlFor="firstName"
              className="mb-2 field-label block text-sm font-normal text-white"
            >
              Verification Code
            </label>
            <Input
              name="verificationCode"
              value={verificationCode}
              onChange={handleInputChange}
              className={`rounded-md border border-solid ${isVerificationCodeValid ? "input-bg" : "validation"} !cursor-text`}
              classNames={{
                inputWrapper: [
                  "!cursor-text",
                  "rounded-md",
                  "border",
                  "border-solid",
                  "border-white",
                  "border-opacity-50",
                  "bg-white-5",
                ],
              }}
            ></Input>
            {!isVerificationCodeValid && (
              <p className="validation-txt mt-1 text-sm">
                *Verification code is wrong. Please resend and try again
              </p>
            )}
            {isVerificationCodeValid && (
              <p className="mt-1 text-sm font-light font-normal text-white text-opacity-30">
                *The password has been sent to your registered email
              </p>
            )}
          </div>
          <div className="grid basis-1/2 grid-rows-2">
            <div className="flex items-end justify-end">
              <Button className="font-ubuntu w-1 rounded-md bg-white bg-opacity-20 pl-20 pr-20 text-base font-bold">
                Resend Code
              </Button>
            </div>
            <div className="mt-2.5 flex justify-center">
              <span className="tb-data-blue text-sm sm:text-lg md:text-lg lg:text-lg">
                Resend In 04:12
              </span>
            </div>
          </div>
        </div>
        {/* hidden in the mobile view */}
        <div className="hidden sm:flex sm:flex-row">
          <div className="basis-3/4">
            <label
              htmlFor="firstName"
              className="mb-2 field-label block text-sm font-normal text-white"
            >
              Verification Code
            </label>
            <Input
              name="verificationCode"
              value={verificationCode}
              onChange={handleInputChange}
              className={`rounded-md border border-solid ${isVerificationCodeValid ? "input-bg" : "validation"} !cursor-text`}
              classNames={{
                inputWrapper: [
                  "!cursor-text",
                  "rounded-md",
                  "border",
                  "border-solid",
                  "border-white",
                  "border-opacity-50",
                  "bg-white-5",
                ],
              }}
            ></Input>
            {!isVerificationCodeValid && (
              <p className="validation-txt mt-1 text-sm">
                *Verification code is wrong. Please resend and try again
              </p>
            )}
            {isVerificationCodeValid && (
              <p className="mt-1 text-sm font-light text-white text-opacity-30">
                *The password has been sent to your registered email
              </p>
            )}
          </div>
          <div className="flex basis-1/4 items-center pl-10">
            <div className="m-1 basis-1/2">
              <span className="tb-data-blue">Resend In 04:12</span>
            </div>
            <div className="m-1 basis-1/2">
              <Button className="font-ubuntu w-1 rounded-md bg-white bg-opacity-20 pl-20 pr-20 text-base font-bold">
                Resend Code
              </Button>
            </div>
          </div>
        </div>
        <div className="margin-top-negative-40 flex w-full flex-row sm:justify-end">
          <div className="mt-10 sm:float-right sm:mt-0">
            <Button
              onClick={handleSave}
              className="admin-common-button-gradient font-ubuntu mr-1 w-1 rounded-md pl-44 pr-44 text-base font-bold sm:pl-20 sm:pr-20"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetAccountPw;
